import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { authApi } from '@/lib/api'
import { api } from '@/lib/api-client'
import { User, Phone, MessageCircle, Mail, Save, KeyRound, Eye, EyeOff, Bell } from 'lucide-react'

export default function ProfilePage() {
  const queryClient = useQueryClient()
  const { data: profileData, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => authApi.getProfile(),
  })
  const profile = profileData?.data

  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', whatsapp: '' })
  const [formInitialized, setFormInitialized] = useState(false)
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' })
  const [showPw, setShowPw] = useState(false)
  const [saved, setSaved] = useState(false)
  const [pwError, setPwError] = useState('')

  // Initialize form once profile loads
  if (profile && !formInitialized) {
    setForm({
      first_name: profile.first_name ?? '',
      last_name: profile.last_name ?? '',
      phone: profile.phone ?? '',
      whatsapp: profile.whatsapp ?? '',
    })
    setFormInitialized(true)
  }

  const updateMutation = useMutation({
    mutationFn: () => authApi.updateProfile(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    },
  })

  const changePwMutation = useMutation({
    mutationFn: () => authApi.changePassword(pwForm.current, pwForm.next),
    onSuccess: () => {
      setPwForm({ current: '', next: '', confirm: '' })
      setPwError('Palavra-passe alterada com sucesso!')
      setTimeout(() => setPwError(''), 3000)
    },
    onError: (e: any) => {
      setPwError(e?.response?.data?.detail ?? 'Erro ao alterar palavra-passe')
    },
  })

  const handlePwSubmit = () => {
    if (pwForm.next !== pwForm.confirm) { setPwError('As palavras-passe não coincidem'); return }
    if (pwForm.next.length < 8) { setPwError('Mínimo 8 caracteres'); return }
    setPwError('')
    changePwMutation.mutate()
  }

  if (isLoading) return <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" /></div>

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold text-gray-900">Perfil</h1>
      <p className="mt-1 text-sm text-gray-500">Informações pessoais e segurança da conta</p>

      {/* Avatar + email (read-only) */}
      <div className="mt-6 card flex items-center gap-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-700 text-2xl font-bold">
          {profile?.first_name?.[0]}{profile?.last_name?.[0]}
        </div>
        <div>
          <p className="font-semibold text-gray-900">{profile?.full_name}</p>
          <p className="text-sm text-gray-500">{profile?.email}</p>
          <div className="mt-1 flex gap-2">
            {profile?.roles?.map((r: any) => (
              <span key={r?.name ?? r} className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">{r?.name ?? r}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Edit form */}
      <div className="mt-6 card">
        <h2 className="mb-4 text-base font-semibold text-gray-800">Dados pessoais</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Primeiro nome</label>
            <div className="relative">
              <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input className="input pl-9 w-full" value={form.first_name}
                onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} placeholder="Nome" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Apelido</label>
            <div className="relative">
              <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input className="input pl-9 w-full" value={form.last_name}
                onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} placeholder="Apelido" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input className="input pl-9 w-full bg-gray-50 text-gray-500" value={profile?.email ?? ''} disabled />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefone pessoal</label>
            <div className="relative">
              <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input className="input pl-9 w-full" value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+351 9XX XXX XXX" />
            </div>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Número WhatsApp</label>
            <div className="relative">
              <MessageCircle className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input className="input pl-9 w-full" value={form.whatsapp}
                onChange={e => setForm(f => ({ ...f, whatsapp: e.target.value }))} placeholder="+351 9XX XXX XXX" />
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button className="btn btn-primary flex items-center gap-2" onClick={() => updateMutation.mutate()}
            disabled={updateMutation.isPending}>
            <Save className="h-4 w-4" />
            {updateMutation.isPending ? 'A guardar...' : 'Guardar alterações'}
          </button>
          {saved && <span className="text-sm text-green-600 font-medium">✓ Guardado com sucesso</span>}
          {updateMutation.isError && <span className="text-sm text-red-600">Erro ao guardar</span>}
        </div>
      </div>

      {/* Change password */}
      <div className="mt-6 card">
        <h2 className="mb-4 text-base font-semibold text-gray-800 flex items-center gap-2">
          <KeyRound className="h-4 w-4" /> Alterar palavra-passe
        </h2>
        <div className="space-y-3">
          <div className="relative">
            <input type={showPw ? 'text' : 'password'} className="input w-full pr-10"
              placeholder="Palavra-passe atual" value={pwForm.current}
              onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} />
            <button className="absolute right-3 top-2.5" onClick={() => setShowPw(s => !s)}>
              {showPw ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
            </button>
          </div>
          <input type={showPw ? 'text' : 'password'} className="input w-full"
            placeholder="Nova palavra-passe (mín. 8 caracteres)" value={pwForm.next}
            onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))} />
          <input type={showPw ? 'text' : 'password'} className="input w-full"
            placeholder="Confirmar nova palavra-passe" value={pwForm.confirm}
            onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} />
        </div>
        <div className="mt-3 flex items-center gap-3">
          <button className="btn btn-secondary" onClick={handlePwSubmit} disabled={changePwMutation.isPending}>
            {changePwMutation.isPending ? 'A alterar...' : 'Alterar palavra-passe'}
          </button>
          {pwError && (
            <span className={`text-sm font-medium ${pwError.includes('sucesso') ? 'text-green-600' : 'text-red-600'}`}>
              {pwError}
            </span>
          )}
        </div>
      </div>

      {/* Email notification subscriptions */}
      <NotificationSubscriptions />
    </div>
  )
}

function NotificationSubscriptions() {
  const { data, isLoading } = useQuery({
    queryKey: ['notif-subscriptions'],
    queryFn: () => api.get<{ subscriptions: { notification_type: string; label: string; is_active: boolean }[] }>('/api/settings/notifications'),
  })
  const subs = data?.data?.subscriptions ?? []
  const qc = useQueryClient()

  const toggleMut = useMutation({
    mutationFn: (updates: { notification_type: string; is_active: boolean }[]) =>
      api.put('/api/settings/notifications', updates),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notif-subscriptions'] }),
  })

  function toggle(type: string, current: boolean) {
    toggleMut.mutate([{ notification_type: type, is_active: !current }])
  }

  if (isLoading) return null

  return (
    <div className="mt-6 card">
      <h2 className="mb-1 text-base font-semibold text-gray-800 flex items-center gap-2">
        <Bell className="h-4 w-4" /> Notificações por email
      </h2>
      <p className="mb-4 text-sm text-gray-500">Receber emails automáticos para os eventos selecionados (requer SMTP configurado).</p>
      <div className="space-y-3">
        {subs.map(s => (
          <div key={s.notification_type} className="flex items-center justify-between rounded-lg border border-gray-100 p-3">
            <span className="text-sm text-gray-700">{s.label}</span>
            <button
              type="button"
              onClick={() => toggle(s.notification_type, s.is_active)}
              disabled={toggleMut.isPending}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${s.is_active ? 'bg-primary-600' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${s.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

